import { createSlice, Dictionary, PayloadAction } from '@reduxjs/toolkit';
import { get, set } from 'lodash';
import { DropResult } from 'react-beautiful-dnd';

type Item = string;
type Items = Item[];
export type Matrix = Dictionary<Dictionary<Items>>;

interface BoardState {
  matrix: Matrix;
}
type RowId = string;
type ColumnId = string;
type Cell = [RowId, ColumnId];

const Cell = (rowId: RowId, colId: ColumnId): Cell => [rowId, colId];

const sortItems = (source = [], target = []) => {
  return source.sort(function (a, b) {
    return target.indexOf(a) - target.indexOf(b);
  });
};

export const taskBoardSlice = createSlice({
  initialState: { matrix: {} },
  name: 'task-board',
  reducers: {
    updateMatrix: (
      state: BoardState,
      { payload }: PayloadAction<DropResult>,
    ): BoardState => {
      const result = payload;

      if (!result.destination) {
        return state;
      }
      if (result.type === 'column') {
        return state;
      }

      // reordering in same list
      if (result.source.droppableId === result.destination.droppableId) {
        return state;
      }

      // moving between lists
      const [sourceRowId, sourceColumnId] =
        result.source.droppableId.split('.');
      const [destinationRowId, destinationColumnId] =
        result.destination.droppableId.split('.');

      // do not allow cross update
      if (sourceRowId !== destinationRowId) {
        return state;
      }
      const sourceIndex = result.source.index;
      const sourceColumn = get(state.matrix, [sourceRowId, sourceColumnId], []);
      const destinationColumn = get(
        state.matrix,
        [destinationRowId, destinationColumnId],
        [],
      );
      const item = sourceColumn[sourceIndex];

      // 1. remove item from source column
      const newSourceColumn = [...sourceColumn];

      newSourceColumn.splice(sourceIndex, 1);

      // 2. insert into destination column
      const newDestinationColumn = [...destinationColumn];

      newDestinationColumn.splice(result.destination.index, 0, item);
      const sourceRow = state.matrix[sourceRowId];
      const updated: Matrix = {
        [sourceRowId]: {
          ...sourceRow,
          [sourceColumnId]: newSourceColumn,
          [destinationColumnId]: newDestinationColumn,
        },
      };

      return {
        ...state,
        matrix: {
          ...state.matrix,
          ...updated,
        },
      };
    },
    patchMatrix: (
      state: BoardState,
      { payload }: PayloadAction<Matrix>,
    ): BoardState => {
      const { matrix } = state;
      const hasValue = Object.keys(matrix).length;

      if (hasValue) {
        const existingMatrix = matrix;
        const cells: Cell[] = [];

        Object.keys(payload).forEach((rowId) => {
          const row = payload[rowId];

          if (row) {
            Object.keys(row).forEach((columnId) => {
              cells.push(Cell(rowId, columnId));
            });
          }
        });

        const result: Matrix = {};

        cells.forEach((cell) => {
          const newItems = get(payload, cell, []);
          const existingItems = get(existingMatrix, cell);
          const sorted = existingItems
            ? sortItems(newItems, existingItems)
            : newItems;

          set(result, cell, sorted);
        });

        return {
          ...state,
          matrix: result,
        };
      }

      return {
        ...state,
        matrix: payload,
      };
    },

    createMatrix: (state: BoardState, { payload }: PayloadAction<Matrix>) => {
      return {
        ...state,
        matrix: payload,
      };
    },
  },
});
